import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useAuth } from './AuthContext';
import { trackEvent } from '../hooks/useAnalytics';

const FavoritesContext = createContext(null);

export const useFavorites = () => {
  const context = useContext(FavoritesContext);
  if (!context) {
    throw new Error('useFavorites must be used within a FavoritesProvider');
  }
  return context;
};

export const FavoritesProvider = ({ children }) => {
  const [favorites, setFavorites] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const { isAuthenticated, user } = useAuth();

  const backendUrl = process.env.REACT_APP_BACKEND_URL || '';

  // Fetch favorites when user is authenticated
  const fetchFavorites = useCallback(async () => {
    if (!isAuthenticated) {
      setFavorites([]);
      return;
    }

    setIsLoading(true);
    try {
      const response = await fetch(`${backendUrl}/api/favorites`, {
        credentials: 'include',
      });

      if (response.ok) {
        const data = await response.json();
        setFavorites(data);
      }
    } catch (error) {
      console.error('Fetch favorites error:', error);
    } finally {
      setIsLoading(false);
    }
  }, [isAuthenticated, backendUrl]);

  useEffect(() => {
    fetchFavorites();
  }, [fetchFavorites, user]);

  const addFavorite = async (productId) => {
    if (!isAuthenticated) {
      return { success: false, needsAuth: true };
    }

    try {
      const response = await fetch(`${backendUrl}/api/favorites`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({ product_id: productId }),
      });

      if (response.ok) {
        setFavorites((prev) => [...prev, productId]);
        // Track favorite add event
        trackEvent({
          event_type: 'add_to_favorites',
          product_id: productId,
        });
        return { success: true };
      }
    } catch (error) {
      console.error('Add favorite error:', error);
    }
    return { success: false };
  };

  const removeFavorite = async (productId) => {
    if (!isAuthenticated) {
      return { success: false, needsAuth: true };
    }

    try {
      const response = await fetch(`${backendUrl}/api/favorites/${productId}`, {
        method: 'DELETE',
        credentials: 'include',
      });

      if (response.ok) {
        setFavorites((prev) => prev.filter((id) => id !== productId));
        // Track favorite remove event
        trackEvent({
          event_type: 'remove_from_favorites',
          product_id: productId,
        });
        return { success: true };
      }
    } catch (error) {
      console.error('Remove favorite error:', error);
    }
    return { success: false };
  };

  const toggleFavorite = async (productId) => {
    if (favorites.includes(productId)) {
      return removeFavorite(productId);
    } else {
      return addFavorite(productId);
    }
  };

  const isFavorite = (productId) => {
    return favorites.includes(productId);
  };

  const value = {
    favorites,
    isLoading,
    addFavorite,
    removeFavorite,
    toggleFavorite,
    isFavorite,
    fetchFavorites,
  };

  return <FavoritesContext.Provider value={value}>{children}</FavoritesContext.Provider>;
};
